import type { InjurySeverity, OrthopedicInjuryInventory, SkeletalNode, TrainingAge, TrainingGoal } from '../calibrationTypes'

/** Nodes exposed on the interactive body map (front + back profiles). */
export type BodyMapNodeId = Extract<
  SkeletalNode,
  | 'neck'
  | 'left_shoulder'
  | 'right_shoulder'
  | 'lower_back'
  | 'left_hip'
  | 'right_hip'
  | 'left_knee'
  | 'right_knee'
  | 'left_ankle'
  | 'right_ankle'
>

export type BodyProfile = 'front' | 'back'

export type UiDiscomfortLevel = 'low' | 'medium' | 'high'

export type BodyMapNodeDef = {
  id: BodyMapNodeId
  label: string
  profile: BodyProfile
  /** Normalised SVG coordinate space (0–100). */
  cx: number
  cy: number
  r: number
}

export const TRAINING_GOAL_OPTIONS: { value: TrainingGoal; label: string; hint: string }[] = [
  { value: 'strength', label: 'Strength', hint: 'Max force · lower reps · neural efficiency' },
  { value: 'hypertrophy', label: 'Hypertrophy', hint: 'Muscle growth · volume · progressive overload' },
  {
    value: 'conditioning_weight_loss',
    label: 'Conditioning / Weight loss',
    hint: 'Work capacity · metabolic strain · body comp',
  },
]

export const TRAINING_AGE_OPTIONS: { value: TrainingAge; label: string; hint: string }[] = [
  { value: 'beginner', label: 'Beginner', hint: '< 1 year consistent training' },
  { value: 'intermediate', label: 'Intermediate', hint: '1–3 years · solid movement base' },
  { value: 'advanced', label: 'Advanced', hint: '3+ years · high training literacy' },
]

export const DISCOMFORT_LEVELS: { value: UiDiscomfortLevel; label: string; tone: string }[] = [
  { value: 'low', label: 'Low', tone: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' },
  { value: 'medium', label: 'Medium', tone: 'border-amber-500/50 bg-amber-500/10 text-amber-300' },
  { value: 'high', label: 'High', tone: 'border-red-500/50 bg-red-500/10 text-red-300' },
]

/** Symptom chips surfaced per joint class. */
export const SYMPTOM_PRESETS: Record<BodyMapNodeId, string[]> = {
  neck: ['Stiffness on rotation', 'Headache after training', 'Pinching when overhead'],
  left_shoulder: ['Pinching when overhead', 'Clicking during deep flex', 'Pain on bench press'],
  right_shoulder: ['Pinching when overhead', 'Clicking during deep flex', 'Pain on bench press'],
  lower_back: ['Tightness after sitting', 'Pain on hip hinge', 'Radiating discomfort'],
  left_hip: ['Pinching at depth', 'Groin pull history', 'Limited ROM on squat'],
  right_hip: ['Pinching at depth', 'Groin pull history', 'Limited ROM on squat'],
  left_knee: ['Clicking during deep flex', 'Pain on stairs', 'Swelling post-session'],
  right_knee: ['Clicking during deep flex', 'Pain on stairs', 'Swelling post-session'],
  left_ankle: ['Instability on landing', 'Achilles tightness', 'Limited dorsiflexion'],
  right_ankle: ['Instability on landing', 'Achilles tightness', 'Limited dorsiflexion'],
}

export const BODY_MAP_NODES: BodyMapNodeDef[] = [
  { id: 'neck', label: 'Neck', profile: 'front', cx: 50, cy: 14, r: 5.5 },
  { id: 'left_shoulder', label: 'L Shoulder', profile: 'front', cx: 34, cy: 24, r: 5 },
  { id: 'right_shoulder', label: 'R Shoulder', profile: 'front', cx: 66, cy: 24, r: 5 },
  { id: 'left_hip', label: 'L Hip', profile: 'front', cx: 42, cy: 52, r: 5 },
  { id: 'right_hip', label: 'R Hip', profile: 'front', cx: 58, cy: 52, r: 5 },
  { id: 'left_knee', label: 'L Knee', profile: 'front', cx: 43, cy: 68, r: 4.5 },
  { id: 'right_knee', label: 'R Knee', profile: 'front', cx: 57, cy: 68, r: 4.5 },
  { id: 'left_ankle', label: 'L Ankle', profile: 'front', cx: 43, cy: 86, r: 4 },
  { id: 'right_ankle', label: 'R Ankle', profile: 'front', cx: 57, cy: 86, r: 4 },
  { id: 'neck', label: 'Neck', profile: 'back', cx: 50, cy: 14, r: 5.5 },
  { id: 'left_shoulder', label: 'L Shoulder', profile: 'back', cx: 34, cy: 24, r: 5 },
  { id: 'right_shoulder', label: 'R Shoulder', profile: 'back', cx: 66, cy: 24, r: 5 },
  { id: 'lower_back', label: 'Lower back', profile: 'back', cx: 50, cy: 46, r: 6 },
  { id: 'left_hip', label: 'L Hip', profile: 'back', cx: 42, cy: 52, r: 5 },
  { id: 'right_hip', label: 'R Hip', profile: 'back', cx: 58, cy: 52, r: 5 },
  { id: 'left_knee', label: 'L Knee', profile: 'back', cx: 43, cy: 68, r: 4.5 },
  { id: 'right_knee', label: 'R Knee', profile: 'back', cx: 57, cy: 68, r: 4.5 },
  { id: 'left_ankle', label: 'L Ankle', profile: 'back', cx: 43, cy: 86, r: 4 },
  { id: 'right_ankle', label: 'R Ankle', profile: 'back', cx: 57, cy: 86, r: 4 },
]

export function uiDiscomfortToSeverity(level: UiDiscomfortLevel): InjurySeverity {
  if (level === 'low') return 'mild'
  if (level === 'medium') return 'moderate'
  return 'severe'
}

export function severityToUiDiscomfort(severity: InjurySeverity): UiDiscomfortLevel {
  if (severity === 'mild') return 'low'
  if (severity === 'moderate') return 'medium'
  return 'high'
}

export function legacyGoalToEnum(value: string): TrainingGoal | '' {
  if (value === 'strength' || value === 'hypertrophy') return value
  if (value === 'conditioning' || value === 'conditioning_weight_loss' || value === 'weight_loss') {
    return 'conditioning_weight_loss'
  }
  return ''
}

export function legacyExperienceToTrainingAge(value: string): TrainingAge | '' {
  if (value === 'beginner') return 'beginner'
  if (value === 'advanced') return 'advanced'
  if (value === 'regular' || value === 'intermediate') return 'intermediate'
  return ''
}

export function buildBioPayloadFromInitialData(
  initialData: Record<string, unknown>,
  inventory: OrthopedicInjuryInventory,
): {
  dob: string
  training_goal: TrainingGoal
  training_age: TrainingAge
  orthopedic_injury_inventory: OrthopedicInjuryInventory
  calibration_status: 'not_started'
} | null {
  const dob = String(initialData.dob ?? '')
  const training_goal = legacyGoalToEnum(String(initialData.training_goal ?? ''))
  const training_age = legacyExperienceToTrainingAge(
    String(initialData.training_age ?? initialData.experience_level ?? ''),
  )
  if (!dob || !training_goal || !training_age) return null
  return {
    dob,
    training_goal,
    training_age,
    orthopedic_injury_inventory: inventory,
    calibration_status: 'not_started',
  }
}

export function parseInitialInventory(raw: unknown, legacyNiggles?: string): OrthopedicInjuryInventory {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as OrthopedicInjuryInventory
  }
  if (legacyNiggles?.trim()) {
    return {
      lower_back: {
        severity: 'mild',
        notes: legacyNiggles.trim(),
        tags: ['legacy_niggles_import'],
      },
    }
  }
  return {}
}

export function inventoryNodeCount(inventory: OrthopedicInjuryInventory): number {
  return Object.keys(inventory).length
}
